<script setup>
import { Sparkles, ArrowUpRight } from '@lucide/vue';

defineProps({
    prompts: { type: Array, default: () => [] },
});
defineEmits(['select']);
</script>

<template>
    <div class="flex flex-wrap gap-2">
        <button
            v-for="(prompt, i) in prompts"
            :key="i"
            type="button"
            class="aia-chip group"
            :style="{ '--i': i }"
            @click="$emit('select', prompt)"
        >
            <Sparkles :size="13" class="aia-chip-spark shrink-0" aria-hidden="true" />
            <span>{{ prompt }}</span>
            <ArrowUpRight :size="13" class="aia-chip-arrow shrink-0" aria-hidden="true" />
        </button>
    </div>
</template>

<style scoped>
.aia-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
    border-radius: 9999px;
    border: 1px solid var(--border);
    background: var(--card);
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--foreground);
    transition: transform 200ms cubic-bezier(0.2, 0.65, 0.25, 1),
                border-color 200ms ease, background-color 200ms ease,
                box-shadow 200ms ease;
    /* Staggered entrance, then a slow attention glow (also staggered so the
     * chips shimmer in sequence rather than pulsing in unison). */
    animation:
        aia-chip-in 360ms cubic-bezier(0.2, 0.65, 0.25, 1) backwards,
        aia-chip-glow 3.6s ease-in-out infinite;
    animation-delay:
        calc(var(--i) * 70ms),
        calc(1s + var(--i) * 220ms);
}

.aia-chip:hover {
    transform: translateY(-2px);
    border-color: var(--primary);
    background: var(--accent);
    box-shadow: 0 6px 18px -8px var(--aia-glow, transparent);
}
.aia-chip:active {
    transform: translateY(0);
}

/* Sparkle: tinted with the brand accent, gently scales on hover. */
.aia-chip-spark {
    color: var(--primary);
    transition: transform 200ms ease;
}
.aia-chip:hover .aia-chip-spark {
    transform: scale(1.15) rotate(12deg);
}

/* Arrow is tucked away until hover, then slides in to confirm "tap me". */
.aia-chip-arrow {
    color: var(--muted-foreground);
    max-width: 0;
    opacity: 0;
    margin-left: -0.375rem;
    transition: max-width 220ms ease, opacity 200ms ease, margin-left 220ms ease, transform 200ms ease;
}
.aia-chip:hover .aia-chip-arrow {
    max-width: 1rem;
    opacity: 1;
    margin-left: 0;
    transform: translate(1px, -1px);
}

@keyframes aia-chip-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
}
@keyframes aia-chip-glow {
    0%, 100% { border-color: var(--border); box-shadow: 0 0 0 0 transparent; }
    50%      { border-color: color-mix(in srgb, var(--primary) 55%, transparent);
               box-shadow: 0 0 10px -2px var(--aia-glow, transparent); }
}

@media (prefers-reduced-motion: reduce) {
    .aia-chip { animation: aia-chip-in 1ms both; }
}
</style>
